import {
    ApiGenerationOptions,
    createTradingPortfolioBid,
    getMarketValue,
    getTradingPortfolio,
    getUserPreferences,
    managedInititializationConfig,
    updateUserPreferences
} from '~/networking';
import { postImpactBookACall } from '~/networking/api';
import { getAccountDocumentLink } from '~/networking/api/managed/requests/documents/getAccountDocumentLink';
import { getAccountStatementMonths } from '~/networking/api/managed/requests/documents/getAccountStatementMonths';
import { getAccountStatements } from '~/networking/api/managed/requests/documents/getAccountStatements';
import { postImpactSignup } from '~/networking/api/managed/requests/impact/postImpactSignup';
import {
    cancelOfferListing,
    createOfferListing,
    createTradingListing,
    getOfferListings,
    getOfferTotals,
    getPendingOfferListings,
    patchBulkFmvpeg,
    patchFmvpeg,
    postBulkAutolistWines,
    putOfferListing,
    reserveUpsellOfferGroup
} from '~/networking/api/managed/requests/offers';
import { cancelReservedOffers } from '~/networking/api/managed/requests/offers/cancelReservedOffers';
import { getReservationsInGroup } from '~/networking/api/managed/requests/offers/getReservationsInGroup';
import { getUpsellOfferGroup } from '~/networking/api/managed/requests/offers/getUpsellOfferGroup';
import { purchaseReservedOffers } from '~/networking/api/managed/requests/offers/purchaseReservedOffers';
import { startReservedEscrow } from '~/networking/api/managed/requests/offers/startReservedEscrow';
import {
    createAchAccount,
    createBitpayInvoice,
    createPaymentSubscription,
    createPlaidPaymentIntent,
    createStripePaymentIntent,
    createStripeSecretKey,
    createStripeSubscriptionManagementUrl,
    getPlaidLinkToken,
    getUniqueWireInformation,
    getUserHasSubscriptions,
    getUserPaymentMethods,
    postTransferFunds
} from '~/networking/api/managed/requests/payments';
import {
    cancelBulkLiquidateWines,
    getPortfolioAssetsAsCsv,
    getPortfolioCharges,
    getPortfolioFees,
    getPortfolioWine,
    getUserPortfolio,
    getUserWines,
    patchLiquidationIntent,
    postBulkLiquidateWines
} from '~/networking/api/managed/requests/portfolio';
import { putAllocationClassification } from '~/networking/api/managed/requests/portfolio/putAllocationClassification';
import { putUpdateInvestmentPlan } from '~/networking/api/managed/requests/portfolio/putUpdateInvestmentPlan';
import { putUserProfile } from '~/networking/api/managed/requests/portfolio/putUserProfile';
import {
    createNewUser,
    createWhiskeyvestPortfolio,
    getBankWireKeyReference,
    getUserAllocationPreferences,
    getUserAutoPayStatus,
    getUserCashAwardReferralStatus,
    getUserInformation,
    getUserLinkedBanks,
    getUserPortfolios,
    getUserProfile,
    getUserReferralProfileStatus,
    getUserWithdrawalEligibility,
    patchUserAllocationPreferences,
    postSendInviteEmails,
    postUserLinkedBanks,
    postUserWithdrawal,
    putLinkCashAwardReferral,
    putLinkReferralInviteCode,
    putUserAutoPayCancel,
    putUserAutoPayUpdate,
    putUserInformation,
    putUserPassword
} from '~/networking/api/managed/requests/users';
import { getTradingPortfolioBids } from '~/networking/api/trading/requests/portfolio/getTradingPortfolioBids';
import { getProductPricingHistory } from '~/networking/api/managed/requests/portfolio/getProductPricingHistory';
import { cancelTradingPortfolioBid } from '~/networking/api/trading/requests/portfolio/cancelTradingPortfolioBid';
import { getMarketDirectory, getMarketProductInfo } from '~/hooks/queries/market/fetchers';
import { pollTradingPortfolioBid } from '~/networking/api/trading/requests/portfolio/pollTradingPortfolioBid';

export const makeManagedApis = (managedConfig: ApiGenerationOptions) => {
    const config = managedInititializationConfig(managedConfig);
    return {
        config,
        putLinkCashAwardReferral: putLinkCashAwardReferral(config),
        getUserCashAwardReferralStatus: getUserCashAwardReferralStatus(config),
        createNewUser: createNewUser(config),
        createAchAccount: createAchAccount(config),
        createStripeSecretKey: createStripeSecretKey(config),
        createStripePaymentIntent: createStripePaymentIntent(config),
        createPaymentSubscription: createPaymentSubscription(config),
        createPlaidPaymentIntent: createPlaidPaymentIntent(config),
        createBitpayInvoice: createBitpayInvoice(config),
        createTradingPortfolioBid: createTradingPortfolioBid(config),
        getPlaidLinkToken: getPlaidLinkToken(config),
        getUserHasSubscriptions: getUserHasSubscriptions(config),
        getUserPaymentMethods: getUserPaymentMethods(config),
        getPortfolioFees: getPortfolioFees(config),
        getPortfolioAssetsAsCsv: getPortfolioAssetsAsCsv(config),
        getPortfolioWine: getPortfolioWine(config),
        getProductPricingHistory: getProductPricingHistory(config),
        getUserPortfolio: getUserPortfolio(config),
        getTradingPortfolio: getTradingPortfolio(config),
        getTradingPortfolioBids: getTradingPortfolioBids(config),
        pollTradingPortfolioBid: pollTradingPortfolioBid(config),
        getPendingOfferListings: getPendingOfferListings(config),
        createOfferListing: createOfferListing(config),
        createTradingListing: createTradingListing(config),
        putOfferListing: putOfferListing(config),
        cancelOfferListing: cancelOfferListing(config),
        cancelTradingPortfolioBid: cancelTradingPortfolioBid(config),
        getOfferListings: getOfferListings(config),
        getOfferTotals: getOfferTotals(config),
        getUserWines: getUserWines(config),
        createStripeSubscriptionManagementUrl: createStripeSubscriptionManagementUrl(config),
        getBankWireKeyReference: getBankWireKeyReference(config),
        getUserInformation: getUserInformation(config),
        getUserAutoPayStatus: getUserAutoPayStatus(config),
        putUserAutoPayUpdate: putUserAutoPayUpdate(config),
        putUserAutoPayCancel: putUserAutoPayCancel(config),
        getUserWithdrawalEligibility: getUserWithdrawalEligibility(config),
        getUserAllocationPreferences: getUserAllocationPreferences(config),
        patchUserAllocationPreferences: patchUserAllocationPreferences(config),
        getUserLinkedBanks: getUserLinkedBanks(config),
        postUserLinkedBanks: postUserLinkedBanks(config),
        postUserWithdrawal: postUserWithdrawal(config),
        postTransferFunds: postTransferFunds(config),
        getUserProfile: getUserProfile(config),
        getUserReferralProfileStatus: getUserReferralProfileStatus(config),
        postSendInviteEmails: postSendInviteEmails(config),
        putUserInformation: putUserInformation(config),
        putUserPassword: putUserPassword(config),
        putUpdateInvestmentPlan: putUpdateInvestmentPlan(config),
        putAllocationClassification: putAllocationClassification(config),
        getAccountStatementMonths: getAccountStatementMonths(config),
        getAccountStatements: getAccountStatements(config),
        getAccountDocumentLink: getAccountDocumentLink(config),
        postBulkLiquidateWines: postBulkLiquidateWines(config),
        patchLiquidationIntent: patchLiquidationIntent(config),
        cancelBulkLiquidateWines: cancelBulkLiquidateWines(config),
        putUserProfile: putUserProfile(config),
        postImpactSignup: postImpactSignup(config),
        postImpactBookACall: postImpactBookACall(config),
        putLinkReferralInviteCode: putLinkReferralInviteCode(config),
        getPortfolioCharges: getPortfolioCharges(config),
        getUniqueWireInformation: getUniqueWireInformation(config),
        getUserPreferences: getUserPreferences(config),
        updateUserPreferences: updateUserPreferences(config),
        createWhiskeyvestPortfolio: createWhiskeyvestPortfolio(config),
        getUserPortfolios: getUserPortfolios(config),
        postBulkAutolistWines: postBulkAutolistWines(config),
        patchBulkFvmpeg: patchBulkFmvpeg(config),
        patchFmvpeg: patchFmvpeg(config),
        getUpsellOfferGroup: getUpsellOfferGroup(config),
        reserveUpsellOfferGroup: reserveUpsellOfferGroup(config),
        getReservationsInGroup: getReservationsInGroup(config),
        cancelReservedOffers: cancelReservedOffers(config),
        purchaseReservedOffers: purchaseReservedOffers(config),
        startReservedEscrow: startReservedEscrow(config),
        getMarketValue: getMarketValue(config),
        getMarketDirectory: getMarketDirectory(config),
        getMarketProductInfo: getMarketProductInfo(config)
    };
};

export type ManagedApis = ReturnType<typeof makeManagedApis>;
